<template>
  <div class="flex flex-col gap-4 bg-white">
    <div
      class="sticky z-10 flex flex-initial gap-4 bg-white pb-2"
      :class="multipleSegmentsInSlideIn ? 'top-[4.5rem] pt-0.5 pb-0.5' : 'top-0'"
    >
      <InfuseInput
        :model-value="searchQuery"
        :placeholder="t('iam.permissions.searchPlaceholder')"
        name="search"
        size="xs"
        autofocus
        class="grow-1 flex-1"
        @update:model-value="(q: string) => emit('update:searchQuery', q)"
      >
        <font-awesome-icon :icon="['fas', 'magnifying-glass']" size="1x" class="text-neutral-300" />
        <template #clear="{ value }">
          <font-awesome-icon
            v-if="value"
            :icon="['fas', 'xmark']"
            size="1x"
            class="cursor-pointer text-neutral-300 hover:text-primary-500 active:text-primary-700"
            @click="emit('update:searchQuery', '')"
          />
        </template>
      </InfuseInput>
      <slot name="actions" />
    </div>
    <div v-if="loading" class="mt-8 flex min-w-[80rem] flex-row justify-center">
      <InfuseSpinner size="large" />
    </div>
    <div v-else-if="permissions.length === 0" class="mt-8 flex min-w-[80rem] flex-row justify-center">
      <p class="text-neutral-500">
        {{ t('common.noData') }}
      </p>
    </div>
    <div v-else class="flex min-w-[80rem] flex-col gap-2 text-xs">
      <div
        v-for="root of permissionsPerCategory"
        :key="root.category.id"
        class="flex flex-col gap-4 rounded-md border border-neutral-200 px-6 py-8"
      >
        <div class="flex flex-row gap-2">
          <h2 class="flex-1 text-lg font-bold">{{ root.category.name }}</h2>
          <InfuseButton :color="'primary-border'" @click="emit('clear', root.category.id)">
            {{ t('iam.permissions.clear') }}
          </InfuseButton>
          <InfuseButton :color="'primary-border'" @click="emit('readOnly', root.category.id)">
            {{ t('iam.permissions.readOnly') }}
          </InfuseButton>
          <InfuseButton :color="'primary-border'" @click="emit('all', root.category.id)">
            {{ t('iam.permissions.allPermissions') }}
          </InfuseButton>
        </div>
        <table class="w-full max-w-[80rem]">
          <tbody>
            <template v-for="actionRoot of root.objectTypeActions" :key="actionRoot.objectType">
              <tr class="bg-transparent">
                <td colspan="2" class="pb-4 pt-4 text-base">
                  {{ actionRoot.objectType }}
                </td>
              </tr>
              <template
                v-for="(row, rowIndex) of getPermissionRows(actionRoot.actions)"
                :key="`${actionRoot.objectType}-${rowIndex}`"
              >
                <tr :class="getRowClass(getActionRowIndex(root, actionRoot, rowIndex))">
                  <td v-for="permission of row" :key="permission.id" class="group cursor-pointer px-4 py-2 align-top">
                    <div class="flex flex-row items-center gap-2">
                      <div>
                        <InfuseToggle
                          :id="permission.id"
                          :value="enabledPermissions.includes(permission.id)"
                          @off="emit('remove', permission.id)"
                          @on="emit('add', permission.id)"
                        />
                      </div>
                      <div class="flex flex-1 flex-row gap-2" @click="togglePermission(permission.id)">
                        <h3 class="text-md group-hover:text-primary-600">
                          {{ permission.name }}
                        </h3>

                        <InfuseTooltip
                          :content="permission.description"
                          class="inline-block cursor-help text-primary-500"
                        >
                          <font-awesome-icon :icon="['fal', 'circle-info']" />
                        </InfuseTooltip>
                      </div>
                    </div>
                  </td>
                  <td v-if="row.length === 1" class="px-4 py-2"></td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
        <div class="border-t border-neutral-200"></div>
        <table class="w-full max-w-7xl">
          <thead>
            <tr>
              <th class="w-64 px-4 py-3 text-left text-sm font-semibold text-neutral-700"></th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                {{ t('iam.permissions.accessType.value.SEARCH') }}
              </th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                {{ t('iam.permissions.accessType.value.READ') }}
              </th>
              <th class="px-4 py-3"></th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                {{ t('iam.permissions.accessType.value.CREATE') }}
              </th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                {{ t('iam.permissions.accessType.value.UPDATE') }}
              </th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                {{ t('iam.permissions.accessType.value.DELETE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(crudRoot, crudIndex) of root.objectTypeCrud"
              :key="crudRoot.objectType"
              :class="getRowClass(getCrudRowIndex(root, crudIndex))"
            >
              <td class="w-64 px-4 py-4 align-top">
                {{ crudRoot.objectType }}
              </td>
              <td class="px-4 py-4 align-top">
                <AccessTypeToggle
                  :access-type="AccessType.SEARCH"
                  col-start-class=""
                  :crud-root="crudRoot"
                  :enabled-permissions="enabledPermissions"
                  :add-permission="(id: string) => emit('add', id)"
                  :remove-permission="(id: string) => emit('remove', id)"
                />
              </td>
              <td class="px-4 py-4 align-top">
                <AccessTypeToggle
                  :access-type="AccessType.READ"
                  col-start-class=""
                  :crud-root="crudRoot"
                  :enabled-permissions="enabledPermissions"
                  :add-permission="(id: string) => emit('add', id)"
                  :remove-permission="(id: string) => emit('remove', id)"
                />
              </td>
              <td class="px-4 py-4"></td>
              <td class="px-4 py-4 align-top">
                <AccessTypeToggle
                  :access-type="AccessType.CREATE"
                  col-start-class=""
                  :crud-root="crudRoot"
                  :enabled-permissions="enabledPermissions"
                  :add-permission="(id: string) => emit('add', id)"
                  :remove-permission="(id: string) => emit('remove', id)"
                />
              </td>
              <td class="px-4 py-4 align-top">
                <AccessTypeToggle
                  :access-type="AccessType.UPDATE"
                  col-start-class=""
                  :crud-root="crudRoot"
                  :enabled-permissions="enabledPermissions"
                  :add-permission="(id: string) => emit('add', id)"
                  :remove-permission="(id: string) => emit('remove', id)"
                />
              </td>
              <td class="px-4 py-4 align-top">
                <AccessTypeToggle
                  :access-type="AccessType.DELETE"
                  col-start-class=""
                  :crud-root="crudRoot"
                  :enabled-permissions="enabledPermissions"
                  :add-permission="(id: string) => emit('add', id)"
                  :remove-permission="(id: string) => emit('remove', id)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { AccessType } from '@infuse/api/iam/permission';
import { AccessTypeToggle } from '@infuse/iam/role/ui';
import { InfuseButton, InfuseInput, InfuseSpinner, InfuseToggle, InfuseTooltip } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';
import { Permission } from '@infuse/iam/permission/domain';
import { toRef } from 'vue';
import { usePermissionsCategories } from '@infuse/iam/composables';

const { t } = useI18n();
const emit = defineEmits(['add', 'remove', 'clear', 'readOnly', 'all', 'update:searchQuery']);

const props = defineProps<{
  permissions: Permission[];
  enabledPermissions: string[];
  loading?: boolean;
  searchQuery?: string;
  forceMaxHeight?: boolean;
  multipleSegmentsInSlideIn?: boolean;
}>();

const { permissionsPerCategory } = usePermissionsCategories(toRef(props, 'permissions'));

function togglePermission(permissionId: string) {
  if (props.enabledPermissions.includes(permissionId)) {
    emit('remove', permissionId);
  } else {
    emit('add', permissionId);
  }
}

function getPermissionRows(actions: Permission[]) {
  const rows: Permission[][] = [];
  for (let i = 0; i < actions.length; i += 2) {
    rows.push(actions.slice(i, i + 2));
  }
  return rows;
}

function getTotalActionRowsBefore(root: any, actionRoot: any): number {
  let totalRows = 0;
  for (const objType of root.objectTypeActions) {
    if (objType === actionRoot) break;
    const rows = getPermissionRows(objType.actions);
    totalRows += rows.length;
  }
  return totalRows;
}

function getActionRowIndex(root: any, actionRoot: any, rowIndex: number): number {
  return getTotalActionRowsBefore(root, actionRoot) + rowIndex;
}

function getTotalActionRows(root: any): number {
  let totalRows = 0;
  for (const objType of root.objectTypeActions) {
    const rows = getPermissionRows(objType.actions);
    totalRows += rows.length;
  }
  return totalRows;
}

function getCrudRowIndex(root: any, crudIndex: number): number {
  return getTotalActionRows(root) + crudIndex;
}

function getRowClass(rowIndex: number): string {
  return rowIndex % 2 === 1 ? 'bg-neutral-50' : '';
}
</script>
